import React, { useState } from 'react';
import { useAuth, api } from '../context/AuthContext';
import BrandedPhotoUpload from '../components/BrandedPhotoUpload';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Camera, Share2, Award, Instagram } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

const ShareYourStyle = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [isUploading, setIsUploading] = useState(false);

  const handlePhotoSubmit = async (brandedImage) => {
    if (!currentUser) {
      toast.error('Please sign in to upload photos');
      navigate('/login');
      return;
    }

    setIsUploading(true);
    
    try {
      const response = await api.post('/api/gallery/submit', {
        image_data: brandedImage
      });
      
      toast.success('Photo submitted for review! 🎉', {
        description: 'We\'ll notify you once it\'s approved for the gallery',
        duration: 5000
      });
      
      // Navigate to My Uploads page
      setTimeout(() => {
        navigate('/my-uploads');
      }, 2000);
      
    } catch (error) {
      console.error('Error submitting photo:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to submit photo';
      toast.error(errorMsg);
      throw error; // Re-throw to let component handle loading state
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white py-16">
      <div className="container mx-auto px-4 max-w-4xl">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex justify-center mb-4">
            <Camera className="w-16 h-16 text-lime-400" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-lime-400 to-lime-600">
              Share Your Fresh Style
            </span>
          </h1>
          <p className="text-zinc-400 text-lg max-w-2xl mx-auto">
            Upload your haircut photo and get it branded with the Sauce Sity logo!
            Perfect for sharing on social media. 📸🔥
          </p>
        </div>

        {/* Features */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          <Card className="bg-zinc-900 border-zinc-800">
            <CardHeader>
              <CardTitle className="text-lime-400 text-lg flex items-center gap-2">
                <Camera className="w-5 h-5" />
                Auto-Branded
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-zinc-400 text-sm">
                Your logo is automatically added to the corner of your photo
              </p>
            </CardContent>
          </Card>

          <Card className="bg-zinc-900 border-zinc-800">
            <CardHeader>
              <CardTitle className="text-purple-400 text-lg flex items-center gap-2">
                <Share2 className="w-5 h-5" />
                Easy Sharing
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-zinc-400 text-sm">
                Download and share directly to Instagram, Facebook, or Twitter
              </p>
            </CardContent>
          </Card>

          <Card className="bg-zinc-900 border-zinc-800">
            <CardHeader>
              <CardTitle className="text-lime-400 text-lg flex items-center gap-2">
                <Award className="w-5 h-5" />
                Gallery Entry
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-zinc-400 text-sm">
                Your styled photo can be featured in our gallery showcase
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Upload Component */}
        <BrandedPhotoUpload 
          onPhotoSubmit={currentUser ? handlePhotoSubmit : null}
          title="Upload Your Fresh Cut"
        />

        {/* How It Works */}
        <Card className="bg-gradient-to-r from-purple-900/40 to-black border-purple-500 mt-12">
          <CardHeader>
            <CardTitle className="text-lime-400 text-center">How It Works</CardTitle>
          </CardHeader>
          <CardContent>
            <ol className="space-y-3 text-zinc-300">
              <li className="flex items-start">
                <span className="bg-lime-400 text-black rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5 flex-shrink-0">1</span>
                <span>Upload your fresh haircut photo</span>
              </li>
              <li className="flex items-start">
                <span className="bg-lime-400 text-black rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5 flex-shrink-0">2</span>
                <span>Sauce Sity logo is automatically added to the corner</span>
              </li>
              <li className="flex items-start">
                <span className="bg-lime-400 text-black rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5 flex-shrink-0">3</span>
                <span>Download or share directly to social media</span>
              </li>
              <li className="flex items-start">
                <span className="bg-lime-400 text-black rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5 flex-shrink-0">4</span>
                <span>Tag us @saucesity on Instagram for a chance to be featured!</span>
              </li>
            </ol>
          </CardContent>
        </Card>

        {/* Social Media CTA */}
        <div className="text-center mt-12">
          <Card className="bg-zinc-900 border-zinc-800 inline-block">
            <CardContent className="p-6">
              <Instagram className="w-12 h-12 text-lime-400 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-white mb-2">Tag Us on Instagram!</h3>
              <p className="text-zinc-400 mb-4">
                Share your branded photo and tag <span className="text-lime-400 font-semibold">@saucesity</span>
              </p>
              <p className="text-zinc-500 text-sm">
                Get featured in our gallery and win free services!
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ShareYourStyle;
